import { useState, useEffect } from 'react';
import { WaiterBumpWarningModal } from './WaiterBumpWarningModal';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface WaiterBumpWarningEvent {
  ticketId: string;
  customerName: string;
  scheduledTime: Date;
  bumpAmount: number;
  flexThreshold: number;
  techId: string;
  techName: string;
}

export function WaiterBumpWarningContainer() {
  const [modalOpen, setModalOpen] = useState(false);
  const [eventData, setEventData] = useState<WaiterBumpWarningEvent | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const handleWarning = (event: CustomEvent<WaiterBumpWarningEvent>) => {
      setEventData(event.detail);
      setModalOpen(true);
    };

    window.addEventListener('waiter-bump-warning' as any, handleWarning);
    return () => window.removeEventListener('waiter-bump-warning' as any, handleWarning);
  }, []);

  const handleReassignWaiter = async () => {
    if (!eventData) return;

    // Open OptiPlaceModal for the Waiter ticket
    setModalOpen(false);
    
    // Trigger opti-place modal by dispatching event
    window.dispatchEvent(new CustomEvent('open-opti-place', {
      detail: { ticketId: eventData.ticketId }
    }));
  };

  const handleReassignBlockers = async () => {
    if (!eventData) return;

    try {
      // Chronomancer R-WAITER-6: scope blocker search to the same shop-day
      // as the Waiter's scheduled time. Previously the query filtered by
      // tech + status + `start_time < scheduledTime` only — on multi-day
      // boards (tomorrow's tickets visible, or cross-day navigation) the
      // query could return yesterday's or last-week's tickets whose
      // start_time was still numerically less than today's Waiter time,
      // leading the advisor into reassigning an unrelated historical
      // ticket. The Waiter-bump context is always today, so derive a
      // shop-local scheduled_date from the event's scheduledTime and
      // filter on it. Also exclude the Waiter itself from results.
      const scheduledDate = eventData.scheduledTime.toISOString().slice(0, 10);
      const { data: blockingTickets, error } = await supabase
        .from('tickets')
        .select('id, customer_name, start_time, predicted_minutes')
        .eq('assigned_tech_id', eventData.techId)
        .eq('status', 'Active')
        .eq('scheduled_date', scheduledDate)
        .neq('id', eventData.ticketId)
        .lt('start_time', eventData.scheduledTime.toISOString())
        .order('start_time', { ascending: true });

      if (error) throw error;

      if (blockingTickets && blockingTickets.length > 0) {
        setModalOpen(false);
        
        // Show toast with blocking tickets info
        toast({
          title: '🔄 Reassigning Blocking Tickets',
          description: `Found ${blockingTickets.length} ticket(s) blocking ${eventData.customerName}. Use OptiPlace to reassign them.`,
          duration: 8000,
        });

        // Trigger reassignment for first blocking ticket (user can continue for others)
        window.dispatchEvent(new CustomEvent('open-opti-place', {
          detail: { ticketId: blockingTickets[0].id }
        }));
      } else {
        toast({
          title: 'No Blocking Tickets',
          description: 'No active tickets found before the Waiter appointment.',
          variant: 'default',
        });
        setModalOpen(false);
      }
    } catch (error) {
      console.error('Error finding blocking tickets:', error);
      toast({
        title: 'Error',
        description: 'Failed to find blocking tickets',
        variant: 'destructive',
      });
      setModalOpen(false);
    }
  };

  if (!eventData) return null;

  return (
    <WaiterBumpWarningModal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      ticketId={eventData.ticketId}
      customerName={eventData.customerName}
      scheduledTime={eventData.scheduledTime}
      bumpAmount={eventData.bumpAmount}
      flexThreshold={eventData.flexThreshold}
      techId={eventData.techId}
      techName={eventData.techName}
      onReassignWaiter={handleReassignWaiter}
      onReassignBlockers={handleReassignBlockers}
    />
  );
}
